import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { createViewYardiImport } from "@/modules/rev_eu/master_data/views/ViewYardiImport";
import type { TYardiImportStatus, TYardiRevalidation } from "@/store";
import type {
    TApiYardiImportStatus,
    TApiRevalidationStatus,
    TApiYardiFileUploadResponse,
} from "@/apis/proda_finance";
import { NOOP } from "@/utils/utils";
import { pollUntil, POLL_INTERVAL_MS, POLL_MAX_ATTEMPTS } from "@/utils/pollUntil";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";

const mapStatus = (status: TApiYardiImportStatus): TYardiImportStatus => ({
    files: status.files.map((file) => ({
        sourceTable: file.source_table,
        label: file.label,
        present: file.present,
        rowCount: file.row_count,
        uploadedAt: file.uploaded_at,
        parseOk: file.parse_ok,
        parseStatus: file.parse_status,
        parseError: file.parse_error,
    })),
    tabs: status.tabs.map((tab) => ({
        tab: tab.tab,
        requiredFiles: tab.required_files,
        missingFiles: tab.missing_files,
        ready: tab.ready,
        generatedAt: tab.generated_at,
        isStale: tab.is_stale,
        blockedBy: tab.blocked_by,
        generationStatus: tab.generation_status,
        generationError: tab.generation_error,
        counts: tab.counts,
        warnings: tab.warnings,
        warningCount: tab.warning_count,
        parsingFiles: tab.parsing_files,
    })),
});

const mapRevalidation = (status: TApiRevalidationStatus): TYardiRevalidation => ({
    status: status.status,
    total: status.total,
    processed: status.processed,
    changed: status.changed,
    failed: status.failed,
    eligible: status.eligible,
});

// The polls below that are still hand-rolled (the pending-file wait and the
// upload's own status wait) need these directly. The cadence is the shared one
// `pollUntil` applies, imported rather than re-declared so the two cannot drift.
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const wireSettingsYardiImport = ({ store: _store, useAppStore, api }: TCompositionDeps) => {
    const ViewYardiImport = createViewYardiImport({
        useCases: () => {
            const statusState = useAppStore((state) => state.yardiImportStatusState);
            const revalidationState = useAppStore((state) => state.yardiRevalidation);
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const userPermissions = useAppStore((state) => state.user?.permissions ?? []);
            const setYardiImportLoading = useAppStore((state) => state.setYardiImportLoading);
            const setYardiImportStatus = useAppStore((state) => state.setYardiImportStatus);
            const setYardiImportError = useAppStore((state) => state.setYardiImportError);
            const setYardiRevalidation = useAppStore((state) => state.setYardiRevalidation);
            const resetYardiImport = useAppStore((state) => state.resetYardiImport);

            // Monotonic write-ordering guard shared by every writer of
            // yardiImportStatusState below: loadStatus, onUploadFile (its initial
            // staging write and its own poll), onGenerate (its initial write and its
            // own poll), and the background pending-file poll further down. All of
            // them call an endpoint that returns a status snapshot and then write it
            // through setYardiImportStatus, and none of those calls are mutually
            // exclusive — a tab generating and a different file still parsing are
            // triggered independently and can legitimately be in flight together.
            // Any pair of them can resolve out of dispatch order: an earlier
            // dispatch that happens to take longer landing after a later, faster
            // one. Without ordering, whichever happens to resolve last simply wins,
            // even if it is the stale one — silently reverting a fresher write
            // (e.g. reverting a "succeeded" file back to "pending" right after the
            // caller may have already shown a success toast for it).
            //
            // Guarding only some of these writers and not others would leave the
            // design asymmetric — a future reader would have to reconstruct which
            // writes are safe and why. So every writer here participates the same
            // way, with one deliberate, commented exception below (the company-
            // switch reset, which must win unconditionally rather than compete).
            //
            // `statusDispatchSeqRef` is bumped and captured *at dispatch time*,
            // immediately before each getStatus()/uploadFile()/generate() call —
            // capturing it after the await would prove nothing, since by then the
            // race has already happened. A response is applied only if no
            // higher-numbered dispatch has already written (`statusWrittenSeqRef`),
            // so only the response from the truly latest dispatch ever lands,
            // regardless of resolution order. The counters are shared (not
            // per-writer) because any writer can be the stale one relative to any
            // other — a per-writer counter cannot order writes against a different
            // writer's writes.
            //
            // This cannot deadlock the store on a stale value: at least one
            // response always has the highest sequence number seen so far and is
            // applied (`statusWrittenSeqRef` starts at 0, so the very first
            // response to arrive always qualifies), and a dropped write never stops
            // the writer's own poll loop (where it has one) from continuing to
            // fetch and eventually re-applying the true current status.
            const statusDispatchSeqRef = useRef(0);
            const statusWrittenSeqRef = useRef(0);
            const nextStatusSeq = useCallback(() => ++statusDispatchSeqRef.current, []);
            // Returns whether this response actually got written. A caller that
            // uses the response's own contents to decide anything else (e.g.
            // whether to keep polling) must gate that decision on this return
            // value too — a dropped write means some fresher response already
            // told the store the truth, so this response's own contents are
            // known-stale and must not be trusted for anything, including a
            // "nothing left to do" conclusion.
            const applyStatusIfLatest = useCallback(
                (seq: number, current: TApiYardiImportStatus): boolean => {
                    if (seq <= statusWrittenSeqRef.current) return false;
                    statusWrittenSeqRef.current = seq;
                    setYardiImportStatus(mapStatus(current));
                    return true;
                },
                [setYardiImportStatus],
            );
            // The one deliberate exception: a company switch resetting to a fresh
            // company's state must win unconditionally, not compete on sequence
            // number — it is not itself a response to a dispatched fetch, so it has
            // no natural seq to take, and semantically the reset (and the reload it
            // triggers) always represents the truth for the newly-active company
            // regardless of what any older, still-in-flight fetch for the
            // *previous* company later returns. What still needs guarding is the
            // aftermath: a stale fetch dispatched before the switch must not be
            // allowed to land afterward and show the old company's data under the
            // new company. Raising the floor (not just the dispatch counter, but
            // the written-floor those older dispatches are compared against)
            // guarantees exactly that: everything dispatched before the switch now
            // has a seq at or below the floor and is dropped on arrival, no matter
            // how late.
            const raiseStatusWriteFloor = useCallback(() => {
                statusWrittenSeqRef.current = ++statusDispatchSeqRef.current;
            }, []);

            const loadStatus = useCallback(async () => {
                setYardiImportLoading();
                // Captured before the fetch — see the guard's definition above for
                // why that ordering matters.
                const seq = nextStatusSeq();
                try {
                    const data = await api.yardiImport.getStatus();
                    applyStatusIfLatest(seq, data);
                } catch (error) {
                    setYardiImportError(
                        error instanceof Error
                            ? error
                            : new Error("Failed to load Yardi import status"),
                    );
                }
            }, [setYardiImportLoading, setYardiImportError, nextStatusSeq, applyStatusIfLatest]);

            // Poll revalidation progress until the run leaves the "running" state.
            // pollUntil's default cadence/cap (see its module doc) exceeds the
            // server's stuck-run reclaim window so an interrupted run is still
            // observed here.
            const pollRevalidation = useCallback(async () => {
                await pollUntil(
                    async () => {
                        const current = await api.files.revalidationStatus();
                        setYardiRevalidation(mapRevalidation(current));
                        return current;
                    },
                    (current) => current.status !== "running",
                );
            }, [setYardiRevalidation]);

            const loadRevalidation = useCallback(async () => {
                try {
                    const current = await api.files.revalidationStatus();
                    setYardiRevalidation(mapRevalidation(current));
                    if (current.status === "running") {
                        void pollRevalidation();
                    }
                } catch {
                    // Non-fatal: the button simply stays enabled if we cannot read status.
                }
            }, [setYardiRevalidation, pollRevalidation]);

            // True while onUploadFile's own poll (below) is in flight for a file it
            // just staged. The background poll just below checks this so the two
            // never fetch status concurrently for the same reason.
            const uploadPollActiveRef = useRef(false);

            // A file can stay "pending" far longer than any single poll waits for
            // it: onUploadFile's own poll below gives up after POLL_MAX_ATTEMPTS
            // (~17 minutes), but a queued-and-never-yet-claimed run can legitimately
            // stay pending for PARSE_QUEUE_STALE_AFTER (2 hours, server-side) — and a
            // remount mid-parse shows whatever the store last held, frozen, since
            // status was previously only fetched once on mount. This effect covers
            // both: it runs whenever the last-fetched status has a pending file,
            // keeps re-fetching status until none are, and stops on its own either
            // way (no pending file left, or the component unmounts). It reuses the
            // same sleep/interval and getStatus/mapStatus/setYardiImportStatus as
            // every other poll in this file rather than inventing new machinery.
            //
            // Invariant this loop must hold: it must not stop while the store still
            // shows a pending file, unless the component unmounts. Two things follow
            // from that, and both are load-bearing:
            //
            // 1. The stop decision must never be based on a response this loop knows
            //    is stale. `applyStatusIfLatest` can drop this fetch's own response
            //    as superseded by a fresher one from elsewhere (a different upload,
            //    a generate() poll, another instance of this same poll after a
            //    remount) — in that case this response's "nothing pending" is not
            //    evidence of anything, because the store might still show pending on
            //    account of the *other*, fresher write. So the stop check below only
            //    fires when this fetch's own write actually applied. If it did not,
            //    looping again is always safe: either the fresher write already
            //    flipped hasPendingFile to false, in which case React tears this
            //    effect down via the dependency array below before or shortly after
            //    this next iteration runs — or it did not, in which case there is
            //    still a genuinely pending file and looping is exactly correct.
            // 2. A transient fetch failure must not be terminal. There is no other
            //    mechanism to revive this loop: hasPendingFile does not change just
            //    because a fetch failed, so the dependency array will not re-run this
            //    effect, and "Try Again" only calls loadStatus() once — neither
            //    re-arms a dead poll. So a failed fetch is treated the same as a
            //    successful one that happened to be superseded: skip this iteration
            //    and try again next cycle, paced by the same POLL_INTERVAL_MS as
            //    every other iteration (a de facto backoff — no tighter retry loop
            //    is introduced). This never gives up on its own; the only exits are
            //    an applied response with nothing pending, or unmount.
            const hasPendingFile =
                statusState.data?.files.some((f) => f.parseStatus === "pending") ?? false;
            useEffect(() => {
                if (!hasPendingFile) return;
                let cancelled = false;
                const poll = async () => {
                    while (!cancelled) {
                        await sleep(POLL_INTERVAL_MS);
                        if (cancelled) return;
                        // onUploadFile is already polling this exact file; avoid a
                        // redundant concurrent fetch while that is in flight.
                        if (uploadPollActiveRef.current) continue;
                        // Captured before the fetch, not after — see the guard's
                        // definition above for why that ordering matters.
                        const seq = nextStatusSeq();
                        try {
                            const current = await api.yardiImport.getStatus();
                            if (cancelled) return;
                            const applied = applyStatusIfLatest(seq, current);
                            // Only trust "nothing pending" from a response that
                            // actually landed — see the invariant note above.
                            if (
                                applied &&
                                !current.files.some((f) => f.parse_status === "pending")
                            ) {
                                return;
                            }
                        } catch {
                            // Transient fetch failure: retry rather than give up —
                            // see the invariant note above for why giving up here
                            // would freeze the page with no way to recover short of
                            // a full reload. Falls through to the top of the loop
                            // and tries again after the same POLL_INTERVAL_MS sleep
                            // as any other iteration.
                        }
                    }
                };
                void poll();
                return () => {
                    cancelled = true;
                };
            }, [hasPendingFile, nextStatusSeq, applyStatusIfLatest]);

            // (Re)load on mount and whenever the active company changes. The
            // Import page stays mounted across a company switch, and the store is
            // shared, so the previous company's uploaded/generated status would
            // otherwise keep rendering. The API client sends the current company
            // per request, so a reload fetches the right company's data.
            //
            // The status is reset first so no stale/cross-company data is shown
            // while the new company's (possibly empty) status loads — the loading
            // placeholder is shown instead. This runs in a layout effect (before
            // paint) so the previous company's "Uploaded"/"Ready" badges never
            // flash. loadStatus() sets the pending state synchronously, so the
            // reset does not leave the view in an error/empty state.
            //
            // raiseStatusWriteFloor() first, so that any fetch still in flight from
            // before this switch (dispatched under the previous company) cannot
            // land afterward and show that company's data under this one — see the
            // guard's definition above for why the reset is exempt from the
            // sequence check itself but still has to participate in the bookkeeping.
            useLayoutEffect(() => {
                raiseStatusWriteFloor();
                resetYardiImport();
                void loadStatus();
                void loadRevalidation();
            }, [
                effectiveCompanyId,
                loadStatus,
                loadRevalidation,
                resetYardiImport,
                raiseStatusWriteFloor,
            ]);

            return {
                onMount: NOOP,
                onUnmount: NOOP,
                useStatus: () => statusState,
                useRevalidation: () => revalidationState,
                onRefresh: useCallback(() => {
                    void loadStatus();
                    void loadRevalidation();
                }, [loadStatus, loadRevalidation]),
                onUploadFile: useCallback(
                    async (sourceTable: string, file: File) => {
                        // Upload only stages the file now; the worker parses it, so
                        // poll until the slot leaves "pending" exactly as the
                        // generation flow does.
                        //
                        // Flagged for the background pending-file poll above *before*
                        // the staging dispatch below, not just around the poll loop —
                        // raising it only once the poll loop started left a window,
                        // for the whole duration of the staging request, where the
                        // background poll could still fire a redundant/racy fetch of
                        // its own.
                        uploadPollActiveRef.current = true;
                        let result: TApiYardiFileUploadResponse;
                        let current: TApiYardiImportStatus;
                        try {
                            // Captured before the dispatch — see the guard's
                            // definition above for why that ordering matters. This
                            // staging write is a response the same as any other: a
                            // background poll fetch dispatched just before this
                            // upload started can still be in flight and land after it.
                            const stagingSeq = nextStatusSeq();
                            result = await api.yardiImport.uploadFile(sourceTable, file);
                            applyStatusIfLatest(stagingSeq, result.status);
                            current = result.status;
                            for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
                                // Captured before the fetch — see the guard's
                                // definition above for why that ordering matters.
                                const seq = nextStatusSeq();
                                current = await api.yardiImport.getStatus();
                                applyStatusIfLatest(seq, current);
                                const stillParsing = current.files.some(
                                    (f) =>
                                        f.source_table === sourceTable &&
                                        f.parse_status === "pending",
                                );
                                if (!stillParsing) break;
                                await sleep(POLL_INTERVAL_MS);
                            }
                        } finally {
                            uploadPollActiveRef.current = false;
                        }
                        // `result` is the 202 staging response: success/row_count describe
                        // acceptance, not the parse outcome, which isn't known yet at that
                        // point. By now the poll above has resolved the real outcome, so
                        // rebuild the response from the final polled file entry — otherwise
                        // the caller's toast could claim success for an upload that just
                        // failed to parse.
                        const finalFile = current.files.find((f) => f.source_table === sourceTable);
                        if (!finalFile) return result;
                        // `success` must mean the parse actually finished successfully — a
                        // slot still "pending" once the poll above gives up (the worker
                        // hasn't claimed/finished the run yet) is neither a success nor a
                        // hard failure, so it must not be reported as either. The caller
                        // (createViewYardiImport's handleFileChange) reads `status` to tell
                        // "failed" and "still pending" apart and shows a truthful message
                        // for each.
                        return {
                            ...result,
                            success: finalFile.parse_status === "succeeded",
                            row_count: finalFile.row_count,
                            errors:
                                finalFile.parse_status === "failed" && finalFile.parse_error
                                    ? [finalFile.parse_error]
                                    : [],
                            status: current,
                        };
                    },
                    [nextStatusSeq, applyStatusIfLatest],
                ),
                onGenerate: useCallback(
                    async (tab: string) => {
                        // Enqueue (returns immediately with the tab marked "generating"),
                        // then poll status until the background run finishes. This poll
                        // is not mutually exclusive with the background pending-file
                        // poll or a different in-flight upload — generating one tab and
                        // parsing another file are independently triggered — so, like
                        // every other writer here, both the initial write and each poll
                        // iteration go through the shared sequence guard (see its
                        // definition above) rather than writing unconditionally.
                        const generateSeq = nextStatusSeq();
                        const response = await api.yardiImport.generate(tab);
                        applyStatusIfLatest(generateSeq, response.status);

                        const requested =
                            tab === "all" ? response.status.tabs.map((t) => t.tab) : [tab];
                        // Loop mechanics come from the shared `pollUntil` (same cadence
                        // and attempt cap this file used to hand-roll), but the write
                        // still goes through `applyStatusIfLatest`: several writers race
                        // on import status, so a slow response from an earlier fetch must
                        // not overwrite a newer one.
                        await pollUntil(
                            async () => {
                                const seq = nextStatusSeq();
                                const current = await api.yardiImport.getStatus();
                                applyStatusIfLatest(seq, current);
                                return current;
                            },
                            (current) =>
                                !current.tabs.some(
                                    (t) =>
                                        requested.includes(t.tab) &&
                                        t.generation_status === "generating",
                                ),
                        );
                        return response;
                    },
                    [nextStatusSeq, applyStatusIfLatest],
                ),
                onRevalidateAll: useCallback(async () => {
                    // Start (returns the initial snapshot with the run "running"),
                    // then poll progress until it completes. A 409 means a run is
                    // already in progress — just pick up polling its status.
                    try {
                        const initial = await api.files.revalidateAll();
                        setYardiRevalidation(mapRevalidation(initial));
                    } catch {
                        const current = await api.files.revalidationStatus();
                        setYardiRevalidation(mapRevalidation(current));
                    }
                    await pollRevalidation();
                }, [setYardiRevalidation, pollRevalidation]),
                permissions: () => userPermissions,
            };
        },
    });

    return { ViewYardiImport };
};
